import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  Spinner,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { Tag } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate, productName } from '../lib/format.js';

interface ProductItem {
  id: string;
  sku: string;
  namesI18n: Record<string, string>;
  unit: string;
}

interface PricePeriod {
  id: string;
  basePrice: string;
  currency: string;
  validFrom: string;
  validTo: string | null;
}

interface QuoteBreakdown {
  basePrice: number;
  volumeDiscount: number;
  earlyPaymentDiscount: number;
  netUnitPrice: number;
}

interface QuoteOk {
  breakdown: QuoteBreakdown;
  lineTotal: number;
}

interface QuoteErr {
  error: string;
}

type QuoteResponse = QuoteOk | QuoteErr;

function formatQuoteNumber(value: number, locale: string): string {
  return Number.isFinite(value) ? value.toLocaleString(locale, { maximumFractionDigits: 4 }) : '—';
}

export default function Pricing() {
  const { t, i18n } = useTranslation('pricing');
  const { toast } = useToast();

  const [products, setProducts] = useState<ProductItem[] | null>(null);
  const [productsError, setProductsError] = useState(false);
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('1');
  const [early, setEarly] = useState(false);

  const [quote, setQuote] = useState<QuoteOk | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [periods, setPeriods] = useState<PricePeriod[] | null>(null);
  const [periodsError, setPeriodsError] = useState(false);

  const [basePrice, setBasePrice] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const quoteErrorMessage = useCallback(
    (code: string): string => {
      switch (code) {
        case 'no_active_price':
        case 'unknown_product':
        case 'invalid_input':
          return t(`quoteErrors.${code}`);
        default:
          return t('quoteErrors.generic', { code });
      }
    },
    [t],
  );

  const periodErrorMessage = useCallback(
    (code: string, status: number): string => {
      switch (code) {
        case 'forbidden':
        case 'invalid_input':
        case 'unknown_product':
          return t(`periodErrors.${code}`);
        default:
          return t('formErrors.generic', { code: code || status });
      }
    },
    [t],
  );

  // Produktu saraksts pie ielādes.
  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: ProductItem[] }>('/api/products');
      if (!active) return;
      if (!res.ok) {
        setProductsError(true);
        setProducts([]);
        return;
      }
      const items = res.data.items ?? [];
      setProducts(items);
      if (items[0]) setProductId(items[0].id);
    })();
    return () => {
      active = false;
    };
  }, []);

  const loadQuote = useCallback(async () => {
    if (!productId) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    const qtyNum = Number(qty);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      setQuote(null);
      setQuoteError(t('quoteErrors.quantityPositive'));
      return;
    }
    setQuoteLoading(true);
    setQuoteError(null);
    const res = await api.get<QuoteResponse>(
      `/api/pricing/quote/${productId}?qty=${qtyNum}&earlyPayment=${early}`,
    );
    if ('error' in res.data && res.data.error) {
      setQuote(null);
      setQuoteError(quoteErrorMessage(res.data.error));
    } else if (!res.ok) {
      setQuote(null);
      setQuoteError(t('quoteErrors.loadFailed'));
    } else if ('breakdown' in res.data) {
      setQuote(res.data);
    }
    setQuoteLoading(false);
  }, [productId, qty, early, t, quoteErrorMessage]);

  const loadPeriods = useCallback(async () => {
    if (!productId) {
      setPeriods(null);
      return;
    }
    setPeriodsError(false);
    const res = await api.get<{ items: PricePeriod[] }>(`/api/pricing/periods/${productId}`);
    if (!res.ok) {
      setPeriodsError(true);
      setPeriods([]);
      return;
    }
    setPeriods(res.data.items ?? []);
  }, [productId]);

  useEffect(() => {
    void loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    void loadPeriods();
  }, [loadPeriods]);

  async function onAddPeriod(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!productId) {
      setFormError(t('formErrors.selectProduct'));
      return;
    }
    const priceNum = Number(basePrice);
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setFormError(t('formErrors.basePricePositive'));
      return;
    }
    if (!validFrom) {
      setFormError(t('formErrors.validFromRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ error?: string }>('/api/pricing/periods', {
        productId,
        basePrice: priceNum,
        validFrom: new Date(validFrom).toISOString(),
      });
      if (!res.ok) {
        const msg =
          res.status === 403
            ? t('formErrors.notPermitted')
            : periodErrorMessage(res.data.error ?? '', res.status);
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setBasePrice('');
      setValidFrom('');
      toast(t('addPeriod.success'));
      await Promise.all([loadPeriods(), loadQuote()]);
    } catch {
      const msg = t('formErrors.addFailed');
      setFormError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppLayout title={t('title')}>
      <div className="flex flex-col gap-8">
        {productsError && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {t('loadProductsError')}
          </p>
        )}

        {/* Cenas kalkulators */}
        <Card>
          <CardHeader title={t('calculator.heading')} />
          <CardBody>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <Field label={t('calculator.product')} htmlFor="quote-product" className="flex-1">
                <Select
                  id="quote-product"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                >
                  {products && products.length > 0 ? (
                    products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {productName(p.namesI18n, i18n.language, p.sku)}
                      </option>
                    ))
                  ) : (
                    <option value="">{t('calculator.noProducts')}</option>
                  )}
                </Select>
              </Field>
              <Field label={t('calculator.quantity')} htmlFor="quote-qty">
                <Input
                  id="quote-qty"
                  type="number"
                  min={1}
                  step="any"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="w-28"
                />
              </Field>
              <label className="flex h-10 items-center gap-2 text-sm" htmlFor="quote-early">
                <input
                  id="quote-early"
                  type="checkbox"
                  checked={early}
                  onChange={(e) => setEarly(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-700"
                />
                {t('calculator.earlyPayment')}
              </label>
            </div>

            <div className="mt-4">
              {quoteLoading ? (
                <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Spinner /> {t('calculator.loading')}
                </p>
              ) : quoteError ? (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                  {quoteError}
                </p>
              ) : quote ? (
                <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500 dark:text-slate-400">
                      {t('calculator.basePrice')}
                    </dt>
                    <dd className="font-medium">
                      {formatQuoteNumber(quote.breakdown.basePrice, i18n.language)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500 dark:text-slate-400">
                      {t('calculator.volumeDiscount')}
                    </dt>
                    <dd className="font-medium">
                      {formatQuoteNumber(quote.breakdown.volumeDiscount, i18n.language)}%
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500 dark:text-slate-400">
                      {t('calculator.earlyPaymentDiscount')}
                    </dt>
                    <dd className="font-medium">
                      {formatQuoteNumber(quote.breakdown.earlyPaymentDiscount, i18n.language)}%
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500 dark:text-slate-400">
                      {t('calculator.netUnitPrice')}
                    </dt>
                    <dd className="font-medium">
                      {formatQuoteNumber(quote.breakdown.netUnitPrice, i18n.language)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 sm:col-span-2">
                    <dt className="text-slate-500 dark:text-slate-400">
                      {t('calculator.lineTotal')}
                    </dt>
                    <dd className="font-semibold">
                      {formatQuoteNumber(quote.lineTotal, i18n.language)}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('calculator.empty')}
                </p>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Cenu periodi */}
        <section className="flex flex-col gap-4">
          <h2 className="text-sm font-medium">{t('periods.heading')}</h2>
          {periodsError ? (
            <DataState loading={false} error={t('periods.loadError')} empty={false}>
              {null}
            </DataState>
          ) : Boolean(productId) && periods === null ? (
            <SkeletonRows rows={3} />
          ) : periods !== null && periods.length === 0 ? (
            <EmptyState
              icon={<Tag className="h-6 w-6" />}
              title={t('periods.empty.title')}
              hint={t('periods.empty.hint')}
            />
          ) : periods !== null ? (
            <TableShell>
              <THead>
                <tr>
                  <Th>{t('periods.basePrice')}</Th>
                  <Th>{t('periods.currency')}</Th>
                  <Th>{t('periods.validFrom')}</Th>
                  <Th>{t('periods.validTo')}</Th>
                </tr>
              </THead>
              <tbody>
                {periods.map((p) => (
                  <Tr key={p.id}>
                    <Td className="font-medium text-slate-900 dark:text-white">{p.basePrice}</Td>
                    <Td>{p.currency}</Td>
                    <Td>{formatDate(p.validFrom, i18n.language)}</Td>
                    <Td>{formatDate(p.validTo, i18n.language)}</Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
          ) : null}

          {/* Pievienot cenas periodu */}
          <Card>
            <CardHeader title={t('addPeriod.heading')} />
            <CardBody>
              <form
                className="flex flex-col gap-4 sm:flex-row sm:items-end"
                onSubmit={(e) => void onAddPeriod(e)}
                noValidate
              >
                <Field label={t('addPeriod.basePrice')} htmlFor="period-price">
                  <Input
                    id="period-price"
                    type="number"
                    min={0}
                    step="any"
                    value={basePrice}
                    onChange={(e) => setBasePrice(e.target.value)}
                    className="w-40"
                  />
                </Field>
                <Field label={t('addPeriod.validFrom')} htmlFor="period-from">
                  <Input
                    id="period-from"
                    type="date"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                  />
                </Field>
                <Button type="submit" disabled={submitting || !productId}>
                  {submitting ? t('addPeriod.submitting') : t('addPeriod.submit')}
                </Button>
              </form>
              {formError && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
                  {formError}
                </p>
              )}
            </CardBody>
          </Card>
        </section>
      </div>
    </AppLayout>
  );
}
